import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowRight, Bell, Building2, Calendar, Camera, ChefHat,
  CheckCircle2, Circle, ClipboardList, Cloud, CloudRain, DollarSign, FileText,
  Flag, LayoutDashboard, MapPin, Megaphone, MessageSquare, Music, PowerOff,
  QrCode, Send, Settings, Sparkles, StickyNote, Sun, TriangleAlert, Truck,
  Users, Wallet, Wind, X, Info,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { tabsForEvent } from "../lib/eventPresets";

/* ==========================================================================
 * CommandMode — mission-control day-of workspace for a single event.
 *
 * Redesigned to match the "Event Command Center" mockup: a full-app dark UI
 * with an event-scoped left sidebar, a top status bar, and a grid of purpose-
 * built cards (Current Timeline Block, Next Up, Team & Vendor Status,
 * Communications Center, Incident & Alerts, Event Progress Overview, Weather,
 * Recent Activity, AI Copilot, Notes).
 *
 * The user's global theme is temporarily forced to dark while this route is
 * active; it's restored on unmount.
 * ========================================================================*/

const POLL_MS = 8000;

function useForceDarkTheme() {
  useEffect(() => {
    const body = document.body;
    const wasLight = body.classList.contains("theme-light");
    if (wasLight) body.classList.remove("theme-light");
    return () => { if (wasLight) body.classList.add("theme-light"); };
  }, []);
}

export default function CommandMode() {
  useForceDarkTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [me, setMe] = useState(null);
  const [notes, setNotes] = useState([]);

  // 1s ticking clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${id}/command`);
      setSnap(data);
    } catch { toast.error("Could not load Command Mode"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Weather + current user + persisted notes in parallel
  useEffect(() => {
    let cancelled = false;
    api.get(`/events/${id}/weather/now`).then(({ data }) => {
      if (!cancelled) setWeather(data);
    }).catch(() => {});
    api.get("/auth/me").then(({ data }) => {
      if (!cancelled) setMe(data);
    }).catch(() => {});
    api.get(`/events/${id}/command/notes`).then(({ data }) => {
      if (!cancelled) setNotes(data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  const addNote = async (content) => {
    try {
      const { data } = await api.post(`/events/${id}/command/notes`, { content });
      setNotes((prev) => [...prev, data]);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save note"); }
  };
  const removeNote = async (noteId) => {
    try {
      await api.delete(`/events/${id}/command/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch { toast.error("Could not delete note"); }
  };

  const onEndEvent = async () => {
    if (!window.confirm("End this event? The command view will close and stats will freeze.")) return;
    try {
      await api.post(`/events/${id}/end`);
      toast.success("Event ended");
      navigate(`/events/${id}`);
    } catch { toast.error("Could not end event"); }
  };

  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [kioskOpen, setKioskOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const markComplete = async () => {
    const block = snap?.current_block;
    if (!block) { toast.error("No block in progress"); return; }
    try {
      await api.post(`/events/${id}/timeline/${block.id}/live`, { action: "complete" });
      toast.success(`"${block.title}" marked complete`);
      load();
    } catch { toast.error("Could not mark complete"); }
  };

  const toggleVendor = async (memberId) => {
    try {
      await api.post(`/events/${id}/members/${memberId}/check-in`, {});
      load();
    } catch { toast.error("Check-in failed"); }
  };

  const sendAnnouncement = async (audience, message, priority = "normal") => {
    if (!message.trim()) return false;
    try {
      await api.post(`/events/${id}/announce`, { audience, message, priority });
      toast.success(priority === "emergency" ? "Emergency broadcast sent" : "Announcement sent");
      load();
      return true;
    } catch { toast.error("Send failed"); return false; }
  };

  if (loading || !snap) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="font-mono text-xs tracking-widest uppercase text-white/40">
          Booting Command Mode…
        </div>
      </div>
    );
  }

  const { event, current_block, current_block_started_ms, up_next, timeline, team, activity, issues, metrics } = snap;

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white/90 flex" data-testid="command-mode">
      <CommandSidebar eventId={id} event={event} />

      <div className="flex-1 flex flex-col min-w-0">
        <CommandTopBar
          event={event}
          now={now}
          weather={weather}
          issuesCount={(issues || []).filter((i) => i.priority === "high").length}
          me={me}
          onEnd={onEndEvent}
        />

        <div className="p-6 grid grid-cols-12 gap-4">
          {/* Row 1 */}
          <div className="col-span-12 xl:col-span-6" data-testid="row-current-event">
            <CurrentEventCard
              block={current_block}
              startedMs={current_block_started_ms}
              now={now}
              onComplete={markComplete}
              onAddNote={() => setNoteOpen(true)}
              onLogUpdate={() => setLogOpen(true)}
            />
          </div>
          <div className="col-span-12 xl:col-span-3" data-testid="row-next-up">
            <NextUpCard blocks={up_next} eventId={id} />
          </div>
          <div className="col-span-12 xl:col-span-3 space-y-4" data-testid="row-team-comms">
            <TeamStatusCard team={team} onToggle={toggleVendor} eventId={id} />
            <CommunicationsCard onSend={sendAnnouncement} onEmergency={() => setEmergencyOpen(true)} onKiosk={() => setKioskOpen(true)} />
          </div>

          {/* Row 2 */}
          <div className="col-span-12 xl:col-span-4" data-testid="row-alerts">
            <AlertsFeedCard issues={issues} activity={activity} />
          </div>
          <div className="col-span-12 xl:col-span-8" data-testid="row-progress">
            <EventProgressOverview metrics={metrics} event={event} now={now} />
          </div>

          {/* Row 3 */}
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <WeatherForecastCard weather={weather} />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <RecentActivityCard activity={activity} />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <AICopilotCard metrics={metrics} issues={issues} onNotifyParty={(msg) => sendAnnouncement("clients", msg)} />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <NotesCard notes={notes} onAdd={addNote} onDelete={removeNote} />
          </div>
        </div>
      </div>

      <EmergencyDialog
        open={emergencyOpen}
        onClose={() => setEmergencyOpen(false)}
        onSend={async (msg) => {
          const ok = await sendAnnouncement("all", msg, "emergency");
          if (ok) setEmergencyOpen(false);
        }}
      />
      <KioskDialog open={kioskOpen} onClose={() => setKioskOpen(false)} eventId={id} />
      <BlockNoteDialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        onSave={async (note) => {
          if (!current_block) return;
          try {
            await api.post(`/events/${id}/timeline/${current_block.id}/live`, { live_note: note });
            toast.success("Note added to timeline block");
            setNoteOpen(false);
            load();
          } catch { toast.error("Could not save note"); }
        }}
      />
      <LogUpdateDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onSend={async (msg) => {
          const ok = await sendAnnouncement("all", msg, "normal");
          if (ok) setLogOpen(false);
        }}
      />
    </div>
  );
}

/* ============================== SIDEBAR ================================== */

// Static entries (always shown) — anything else comes from the event preset.
const STATIC_TOP = [
  { id: "dashboard", label: "Dashboard",    icon: LayoutDashboard, to: "/dashboard" },
  { id: "command",   label: "Live Command", icon: Activity,        live: true },
];
const STATIC_BOTTOM = [
  { id: "reports",  label: "Reports",  icon: Flag,     tab: "reports" },
  { id: "settings", label: "Settings", icon: Settings, tab: "overview" },
];

// Map a tab-key (from eventPresets) to the Lucide icon we want in the sidebar.
const TAB_ICONS = {
  overview:  FileText,
  timeline:  ClipboardList,
  guests:    Users,
  vendors:   Users,
  tasks:     CheckCircle2,
  budget:    Wallet,
  documents: FileText,
  chat:      MessageSquare,
  seating:   MapPin,
  setup:     Truck,
  rehearsal: ClipboardList,
  rsvp:      Users,
};

function CommandSidebar({ eventId, event }) {
  // Presets returns an ordered list of visible tabs with family-appropriate
  // labels — sports events say "Teams", corporate says "Attendees", etc.
  const eventTabs = tabsForEvent(event)
    .filter((t) => t.key !== "live") // Live Command is already in STATIC_TOP.
    .map((t) => ({
      id: t.key,
      label: t.label,
      icon: TAB_ICONS[t.key] || FileText,
      tab: t.key,
    }));
  const items = [...STATIC_TOP, ...eventTabs, ...STATIC_BOTTOM];
  return (
    <aside
      className="w-[220px] shrink-0 bg-[#0F0F13] border-r border-white/[0.06] flex flex-col"
      data-testid="command-sidebar"
    >
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-white/[0.06]">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#A78BFA] to-[#7C3AED] flex items-center justify-center">
          <img src="/atelis-icon-192.png" alt="AtelisOS" className="w-5 h-5 object-contain" />
        </div>
        <div className="font-semibold tracking-tight text-white text-[17px]">AtelisOS</div>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.id === "command";
          const linkTo = item.to
            || (item.tab ? `/events/${eventId}?tab=${item.tab}` : `/events/${eventId}`);
          return (
            <Link
              key={item.id}
              to={linkTo}
              data-testid={`sidebar-${item.id}`}
              className={`mx-2 my-0.5 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                active
                  ? "bg-gradient-to-r from-[#7C3AED]/25 to-[#7C3AED]/10 text-white border border-[#7C3AED]/40 shadow-[0_0_12px_rgba(124,58,237,0.15)]"
                  : "text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <Icon size={16} strokeWidth={1.75} className={active ? "text-[#A78BFA]" : ""} />
              <span className="flex-1">{item.label}</span>
              {item.live && (
                <span className="text-[9px] tracking-widest font-mono font-semibold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 uppercase">
                  Live
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
        <span className="text-xs text-white/40 font-medium">AtelisOS</span>
        <span className="text-[10px] font-mono text-white/30">v1.0.0</span>
      </div>
    </aside>
  );
}

/* ============================== TOP BAR ================================== */

function CommandTopBar({ event, now, weather, issuesCount, me, onEnd }) {
  const timeStr = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const initials = (me?.name || me?.email || "").trim().split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "—";
  const systemHealthy = issuesCount === 0;

  return (
    <header
      className="px-6 py-4 border-b border-white/[0.06] bg-[#0A0A0F] flex items-start justify-between gap-6"
      data-testid="command-topbar"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium text-white/50 tracking-wide">Event Command Center</div>
        <div className="mt-1 flex items-center gap-2 text-white text-[15px] font-semibold" data-testid="command-event-title">
          <span className="truncate">{event.title}</span>
        </div>
      </div>

      <div className="text-center px-6">
        <div className="text-[10px] font-medium text-white/50 tracking-widest uppercase">Local Time</div>
        <div className="font-semibold text-[26px] tabular-nums leading-none mt-1" data-testid="command-clock">{timeStr}</div>
        <div className="text-[11px] text-white/50 mt-1">{dateStr}</div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Button
          onClick={onEnd}
          data-testid="command-end-event"
          className="rounded-lg h-9 px-3.5 bg-[#EF4444]/10 hover:bg-[#EF4444]/20 text-[#F87171] border border-[#EF4444]/30 text-[13px] font-medium"
        >
          <PowerOff size={13} strokeWidth={2} className="mr-1.5" /> End Event
        </Button>
      </div>

      {/* Weather chip */}
      <div className="flex items-center gap-2 shrink-0" data-testid="topbar-weather">
        <WeatherMini weather={weather} />
      </div>

      <div className="flex items-center gap-2 shrink-0" data-testid="topbar-status">
        <div className="text-right">
          <div className="text-[11px] font-medium text-white/70">System Status</div>
          <div className="text-[11px] text-emerald-400 flex items-center gap-1.5 justify-end mt-0.5">
            <span className={`w-1.5 h-1.5 rounded-full ${systemHealthy ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
            {systemHealthy ? "All Systems Operational" : `${issuesCount} active issue${issuesCount === 1 ? "" : "s"}`}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2.5 shrink-0" data-testid="topbar-user">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#334155] to-[#1E293B] border border-white/10 flex items-center justify-center text-[11px] font-semibold">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-white truncate max-w-[120px]">{me?.name || "You"}</div>
          <div className="text-[11px] text-white/50 truncate max-w-[120px]">Lead Planner</div>
        </div>
      </div>
    </header>
  );
}

function WeatherMini({ weather }) {
  if (!weather || weather.error) {
    return (
      <div className="text-right">
        <div className="text-[11px] text-white/50">Weather</div>
        <div className="text-[11px] text-white/40">Not configured</div>
      </div>
    );
  }
  const temp = weather.temperature ?? weather.temp;
  const feels = weather.feels_like;
  return (
    <>
      <WeatherGlyph summary={weather.summary} size={38} />
      <div className="text-left">
        <div className="text-[19px] font-semibold tabular-nums leading-none">
          {temp != null ? `${Math.round(temp)}°F` : "—"}
        </div>
        <div className="text-[11px] text-white/60 leading-tight mt-0.5">{weather.summary || "—"}</div>
        {feels != null && <div className="text-[10px] text-white/40 leading-tight">Feels like {Math.round(feels)}°</div>}
      </div>
    </>
  );
}

/* =========================== CURRENT EVENT CARD ========================= */

function CurrentEventCard({ block, startedMs, now, onComplete, onAddNote, onLogUpdate }) {
  const nowMs = now.getTime();

  if (!block) {
    return (
      <Card testid="current-event-card" title="Current Event">
        <div className="p-8 text-center">
          <Sparkles size={20} className="mx-auto text-white/40 mb-2" />
          <div className="text-white/60">No block currently in progress.</div>
          <div className="text-sm text-white/40 mt-1">Advance to the next block from the timeline.</div>
        </div>
      </Card>
    );
  }

  const durationMin = block.duration_min ?? block.duration ?? 30;
  const durationMs = durationMin * 60 * 1000;
  const started = startedMs || nowMs;
  const elapsedMs = Math.max(0, nowMs - started);
  const remainingMs = Math.max(0, durationMs - elapsedMs);
  const percent = Math.max(0, Math.min(100, Math.round((elapsedMs / durationMs) * 100)));

  const startTime = new Date(started).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  const endTime = new Date(started + durationMs).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });

  const mm = String(Math.floor(remainingMs / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, "0");

  return (
    <Card testid="current-event-card" title="Current Event">
      <div className="p-5">
        <div className="bg-[#1A1428] border border-[#7C3AED]/40 rounded-2xl p-6 relative overflow-hidden">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-mono font-semibold px-2.5 py-1 rounded bg-[#7C3AED]/25 text-[#C4B5FD] border border-[#7C3AED]/40">
            CURRENT TIMELINE BLOCK
          </span>

          <div className="mt-4 flex items-start justify-between gap-4">
            <h2 className="font-semibold text-white text-[32px] leading-tight tracking-tight" data-testid="current-block-title">
              {block.title}
            </h2>
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded bg-[#7C3AED]/20 text-[#C4B5FD] border border-[#7C3AED]/30 uppercase tracking-wide font-medium shrink-0">
              In Progress
            </span>
          </div>

          <div className="mt-2 flex items-center gap-2 text-white/70 text-[14px]">
            <Circle size={12} className="text-white/40" />
            <span className="tabular-nums">{startTime} – {endTime}</span>
            <span className="text-white/40">({durationMin} min)</span>
          </div>

          {block.owner_name && (
            <div className="mt-1 flex items-center gap-2 text-white/60 text-[13px]">
              <Users size={12} />
              Owner: <span className="text-white">{block.owner_name}</span>
            </div>
          )}

          <div className="my-5 border-t border-white/[0.06]" />

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div>
              <div className="text-[10px] tracking-widest uppercase font-mono font-semibold text-white/50">
                TIME REMAINING
              </div>
              <div
                className="mt-1 font-semibold text-[42px] tabular-nums leading-none flex items-baseline gap-2"
                data-testid="time-remaining"
              >
                <span>{mm}:{ss}</span>
                <span className="text-[13px] font-normal text-white/50">min</span>
              </div>
            </div>

            <RadialGauge value={percent} />

            <div className="min-w-0">
              <div className="text-[10px] tracking-widest uppercase font-mono font-semibold text-white/50">BLOCK NOTES</div>
              <p className="mt-1 text-[13px] leading-relaxed text-white/80">
                {block.description || block.notes || "No notes for this block."}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2.5">
            <ActionButton onClick={onAddNote} icon={<StickyNote size={14} />} label="Add Note" primary testid="action-add-note" />
            <ActionButton onClick={onLogUpdate} icon={<MessageSquare size={14} />} label="Log Update" testid="action-log-update" />
            <ActionButton onClick={onComplete} icon={<CheckCircle2 size={14} />} label="Block Complete" testid="action-block-complete" />
          </div>
        </div>
      </div>
    </Card>
  );
}

function ActionButton({ onClick, icon, label, primary, testid }) {
  const cls = primary
    ? "bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] hover:from-[#9F72F8] hover:to-[#8B5CF6] text-white border-[#7C3AED]/40 shadow-[0_0_16px_rgba(124,58,237,0.25)]"
    : "bg-white/[0.04] hover:bg-white/[0.08] text-white/90 border-white/[0.08]";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center justify-center gap-2 h-10 rounded-lg border text-[13px] font-medium transition-all ${cls}`}
    >
      {icon} {label}
    </button>
  );
}

function RadialGauge({ value = 0 }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  const dash = c * Math.max(0, Math.min(100, value)) / 100;
  return (
    <div className="relative w-[110px] h-[110px]" data-testid="radial-gauge">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="7" fill="none" />
        <circle
          cx="50" cy="50" r={r}
          stroke="url(#grad-purple)"
          strokeWidth="7"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
        />
        <defs>
          <linearGradient id="grad-purple" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-semibold text-[24px] tabular-nums leading-none">{value}<span className="text-[13px] text-white/50">%</span></span>
        <span className="text-[9px] tracking-widest uppercase font-mono text-white/40 mt-1">Complete</span>
      </div>
    </div>
  );
}

/* ============================== NEXT UP ================================== */

const BLOCK_ACCENTS = ["#F59E0B", "#F59E0B", "#3B82F6", "#EC4899", "#10B981"];

function NextUpCard({ blocks, eventId }) {
  return (
    <Card testid="next-up-card" title="Next Up" subtitle="Upcoming Timeline Blocks">
      <div className="p-4 space-y-2">
        {(blocks || []).length === 0 && (
          <div className="text-white/40 text-sm text-center py-4">Nothing queued — you&apos;re at the end of the run of show.</div>
        )}
        {(blocks || []).slice(0, 3).map((b, i) => (
          <div
            key={b.id}
            className="rounded-xl p-3.5 bg-white/[0.03] border border-white/[0.06] flex items-start gap-3"
            style={{ borderLeftColor: BLOCK_ACCENTS[i % BLOCK_ACCENTS.length], borderLeftWidth: 3 }}
            data-testid={`next-block-${b.id}`}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-semibold shrink-0"
              style={{ background: `${BLOCK_ACCENTS[i % BLOCK_ACCENTS.length]}20`, color: BLOCK_ACCENTS[i % BLOCK_ACCENTS.length] }}
            >
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white text-[14px] leading-tight truncate">{b.title}</div>
              <div className="text-[12px] text-white/60 mt-1 tabular-nums">
                {formatBlockRange(b)} <span className="text-white/40">({b.duration_min ?? b.duration ?? 30} min)</span>
              </div>
              {(b.owner_name || b.owner) && (
                <div className="text-[11px] text-white/40 mt-1 flex items-center gap-1">
                  <Users size={10} /> Owner: <span className="text-white/70">{b.owner_name || b.owner}</span>
                </div>
              )}
            </div>
          </div>
        ))}
        <Link
          to={`/events/${eventId}?tab=timeline`}
          className="flex items-center justify-center gap-1.5 w-full h-10 rounded-lg text-[13px] text-white/60 hover:text-white hover:bg-white/[0.04] transition-colors"
          data-testid="view-full-timeline"
        >
          View Full Timeline <ArrowRight size={13} />
        </Link>
      </div>
    </Card>
  );
}

/* ========================== TEAM & VENDOR STATUS ========================= */

const ROLE_ICONS = {
  coordinator:  Users,
  planner:      Users,
  photographer: Camera,
  videographer: Camera,
  dj:           Music,
  caterer:      ChefHat,
  catering:     ChefHat,
  venue:        Building2,
  florist:      Sparkles,
  transportation: Truck,
  officiant:    Users,
  vendor:       Users,
  client:       Users,
};

function TeamStatusCard({ team, onToggle, eventId }) {
  const statusFor = (m) => {
    if (m.status === "checked_in") return { label: "Ready", color: "emerald" };
    if (m.status === "confirmed")  return { label: "En Route", color: "amber" };
    return { label: "Standby", color: "sky" };
  };
  const colorClasses = {
    emerald: "text-emerald-400",
    amber:   "text-amber-400",
    sky:     "text-sky-400",
  };
  const dotClasses = {
    emerald: "bg-emerald-400",
    amber:   "bg-amber-400",
    sky:     "bg-sky-400",
  };

  return (
    <Card testid="team-status-card" title="Team & Vendor Status" subtitle="Live Readiness">
      <div className="p-4">
        <ul className="space-y-2">
          {(team || []).slice(0, 8).map((m) => {
            const role = (m.role || "").toLowerCase();
            const Icon = ROLE_ICONS[role] || Users;
            const status = statusFor(m);
            return (
              <li
                key={m.id}
                className="flex items-center gap-2.5 py-1"
                data-testid={`team-row-${m.id}`}
              >
                <Icon size={14} strokeWidth={1.75} className="text-white/60 shrink-0" />
                <span className="flex-1 text-[13px] truncate">{m.display_name || m.name || (role || "member").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                <button
                  onClick={() => onToggle?.(m.id)}
                  className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${colorClasses[status.color]}`}
                  data-testid={`team-checkin-${m.id}`}
                  title={m.status === "checked_in" ? "Check out" : "Check in"}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${dotClasses[status.color]}`} />
                  {status.label}
                </button>
              </li>
            );
          })}
          {(!team || team.length === 0) && (
            <li className="text-white/40 text-sm text-center py-3">No team members yet.</li>
          )}
        </ul>
        <Link
          to={`/events/${eventId}?tab=vendors`}
          className="mt-3 flex items-center justify-center gap-1.5 w-full h-9 rounded-lg text-[12px] text-white/60 hover:text-white hover:bg-white/[0.04] border border-white/[0.06] transition-colors"
          data-testid="view-all-teams"
        >
          <Users size={12} /> View All Teams
        </Link>
      </div>
    </Card>
  );
}

/* =========================== COMMUNICATIONS CARD ======================== */

function CommunicationsCard({ onSend, onEmergency, onKiosk }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const commActions = [
    { label: "Announce", sub: "to All", icon: Megaphone, color: "#A78BFA", audience: "all" },
    { label: "Notify",   sub: "Vendors", icon: Bell,     color: "#3B82F6", audience: "vendors" },
    { label: "Notify",   sub: "Wedding Party", icon: Users, color: "#EC4899", audience: "clients" },
    { label: "Message",  sub: "Individuals", icon: MessageSquare, color: "#F59E0B", audience: "custom" },
  ];

  const [audience, setAudience] = useState("all");

  const send = async () => {
    if (!text.trim()) { toast.error("Type an announcement first"); return; }
    setSending(true);
    const ok = await onSend?.(audience, text.trim());
    setSending(false);
    if (ok) setText("");
  };

  return (
    <Card testid="communications-card" title="Communications Center">
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {commActions.map((a) => (
            <button
              key={a.sub}
              onClick={() => setAudience(a.audience)}
              data-testid={`comm-preset-${a.audience}`}
              className={`text-left p-3 rounded-xl border transition-all ${
                audience === a.audience
                  ? "bg-white/[0.05] border-white/[0.14]"
                  : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]"
              }`}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center mb-2"
                style={{ background: `${a.color}20`, color: a.color }}
              >
                <a.icon size={14} strokeWidth={2} />
              </div>
              <div className="text-[12px] font-semibold text-white leading-none">{a.label}</div>
              <div className="text-[11px] text-white/60 leading-tight mt-0.5">{a.sub}</div>
            </button>
          ))}
        </div>

        <div className="pt-2">
          <div className="text-[10px] tracking-widest uppercase font-mono font-semibold text-white/50 mb-1.5">
            Quick Announcement
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type announcement…"
              className="rounded-lg h-9 bg-white/[0.03] border-white/[0.06] text-white placeholder:text-white/30 text-[13px]"
              data-testid="comm-quick-input"
            />
            <button
              onClick={send}
              disabled={sending}
              data-testid="comm-quick-send"
              className="w-9 h-9 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] hover:from-[#9F72F8] flex items-center justify-center shrink-0 disabled:opacity-50"
            >
              <Send size={14} className="text-white" />
            </button>
          </div>
        </div>

        <div className="pt-2 flex items-center gap-2">
          <button
            onClick={onKiosk}
            data-testid="comm-kiosk"
            className="flex-1 h-8 rounded-md text-[11px] text-white/60 hover:text-white hover:bg-white/[0.04] border border-white/[0.06] inline-flex items-center justify-center gap-1.5"
          >
            <QrCode size={11} /> Kiosk
          </button>
          <button
            onClick={onEmergency}
            data-testid="comm-emergency"
            className="flex-1 h-8 rounded-md text-[11px] text-[#F87171] hover:text-white hover:bg-[#EF4444]/20 border border-[#EF4444]/30 inline-flex items-center justify-center gap-1.5"
          >
            <TriangleAlert size={11} /> Emergency
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ========================== ALERTS FEED ================================== */

function AlertsFeedCard({ issues, activity }) {
  // Derive an ordered feed: unresolved issues (high/med) → recent notable activity.
  const alerts = useMemo(() => {
    const items = [];
    (issues || []).forEach((i) => {
      items.push({
        id: i.id,
        state: i.priority === "high" ? "new" : "acknowledged",
        title: i.message,
        subtitle: i.recommended,
        time: i.created_at,
        color: i.priority === "high" ? "red" : "amber",
      });
    });
    (activity || []).slice(0, 3).forEach((a) => {
      items.push({
        id: `act-${a.id || a.timestamp}`,
        state: "resolved",
        title: a.description || a.message,
        subtitle: a.detail,
        time: a.timestamp || a.created_at,
        color: "sky",
      });
    });
    return items.slice(0, 4);
  }, [issues, activity]);

  const count = (issues || []).length;

  return (
    <Card testid="alerts-feed-card" title="Incident & Alerts Feed" badge={count > 0 ? { label: String(count), color: "red" } : null}>
      <div className="p-4">
        {alerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 size={24} className="mx-auto text-emerald-400/70 mb-2" />
            <div className="text-white/60 text-sm">All clear — no issues detected.</div>
          </div>
        ) : (
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li key={a.id} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]" data-testid={`alert-${a.id}`}>
                <AlertIcon state={a.state} color={a.color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[11px] font-mono text-white/50 tabular-nums">{formatShortTime(a.time)}</span>
                    <AlertStateChip state={a.state} />
                  </div>
                  <div className="text-[13px] text-white leading-snug">{a.title}</div>
                  {a.subtitle && <div className="text-[11px] text-white/50 mt-0.5">{a.subtitle}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
        <button
          className="mt-3 w-full h-9 rounded-lg text-[12px] text-white/60 hover:text-white hover:bg-white/[0.04] border border-white/[0.06] inline-flex items-center justify-center gap-1.5"
          data-testid="view-all-alerts"
        >
          View All Alerts
        </button>
      </div>
    </Card>
  );
}

function AlertIcon({ state, color }) {
  const map = {
    red:   { Icon: AlertTriangle, cls: "text-[#EF4444] bg-[#EF4444]/15 border-[#EF4444]/30" },
    amber: { Icon: AlertTriangle, cls: "text-amber-400 bg-amber-500/15 border-amber-500/30" },
    sky:   { Icon: Info,          cls: "text-sky-400 bg-sky-500/15 border-sky-500/30" },
  }[color] || { Icon: Info, cls: "text-white/60 bg-white/[0.05] border-white/[0.08]" };
  const Icon = map.Icon;
  return (
    <span className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${map.cls}`}>
      <Icon size={14} strokeWidth={2} />
    </span>
  );
}

function AlertStateChip({ state }) {
  const map = {
    new:          { label: "New",          cls: "text-[#F87171] bg-[#EF4444]/10 border-[#EF4444]/30" },
    acknowledged: { label: "Acknowledged", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
    resolved:     { label: "Resolved",     cls: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  }[state] || { label: state, cls: "text-white/60 bg-white/[0.05] border-white/[0.06]" };
  return (
    <span className={`text-[10px] tracking-wide uppercase font-medium px-1.5 py-0.5 rounded border ${map.cls}`}>
      {map.label}
    </span>
  );
}

/* ======================= EVENT PROGRESS OVERVIEW ======================== */

function EventProgressOverview({ metrics, event, now }) {
  const m = metrics || {};
  const eventProgress = m.overall_progress || 0;
  const blocksDone = m.completed_blocks || 0;
  const blocksTotal = m.total_blocks || 0;

  const countdown = useCountdown(event, now);

  return (
    <Card testid="event-progress-card" title="Event Progress Overview">
      <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05] flex flex-col items-center">
          <div className="text-[10px] tracking-widest uppercase font-mono font-semibold text-white/50 mb-2">
            EVENT PROGRESS
          </div>
          <RadialGauge value={eventProgress} />
          <div className={`mt-3 text-[11px] font-medium ${m.timeline_adherence >= 90 ? "text-emerald-400" : "text-amber-400"} flex items-center gap-1`}>
            <CheckCircle2 size={12} /> {m.timeline_adherence >= 90 ? "On Schedule" : `${m.timeline_adherence || 0}% Adherence`}
          </div>
        </div>

        <StatBlock
          label="TIMELINE PROGRESS"
          value={`${blocksDone} / ${blocksTotal}`}
          sub="Blocks Complete"
          progress={blocksTotal > 0 ? Math.round((blocksDone / blocksTotal) * 100) : 0}
          barColor="#A78BFA"
        />
        <StatBlock
          label="TASKS"
          value={`${m.tasks_done || 0} / ${m.tasks_total || 0}`}
          sub="Complete"
          progress={m.tasks_total > 0 ? Math.round(((m.tasks_done || 0) / m.tasks_total) * 100) : 0}
          barColor="#10B981"
        />
        <StatBlock
          label="BUDGET"
          value={`$${(m.budget_actual || 0).toLocaleString()}`}
          sub={`of $${(m.budget_planned || 0).toLocaleString()}`}
          progress={m.budget_planned > 0 ? Math.round(((m.budget_actual || 0) / m.budget_planned) * 100) : 0}
          barColor="#F59E0B"
        />
      </div>

      <div className="mx-4 mb-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-widest uppercase font-mono font-semibold text-white/50">
            EVENT COUNTDOWN
          </div>
          <div className="text-[11px] text-white/50">Until <span className="text-white">End of Event</span></div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <CountdownUnit value={countdown.days} label="Days" />
          <CountdownUnit value={countdown.hours} label="Hrs" />
          <CountdownUnit value={countdown.minutes} label="Mins" />
          <CountdownUnit value={countdown.seconds} label="Secs" />
        </div>
      </div>
    </Card>
  );
}

function StatBlock({ label, value, sub, progress, barColor }) {
  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05] flex flex-col">
      <div className="text-[10px] tracking-widest uppercase font-mono font-semibold text-white/50">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-semibold text-[26px] text-white tabular-nums leading-none">{value}</span>
      </div>
      <div className="text-[11px] text-white/50 mt-1">{sub}</div>
      <div className="mt-auto pt-3">
        <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: barColor }} />
        </div>
      </div>
    </div>
  );
}

function CountdownUnit({ value, label }) {
  return (
    <div className="text-center py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]">
      <div className="font-semibold text-[28px] tabular-nums leading-none text-white">
        {String(Math.max(0, value)).padStart(2, "0")}
      </div>
      <div className="text-[10px] tracking-widest uppercase font-mono text-white/40 mt-1">{label}</div>
    </div>
  );
}

function useCountdown(event, now) {
  // Event ends ~12h after event_date (default). If backend supplies an
  // explicit end_at, prefer that.
  return useMemo(() => {
    if (!event?.event_date) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const end = event.end_at
      ? new Date(event.end_at).getTime()
      : new Date(event.event_date + "T20:00:00").getTime();
    const diff = Math.max(0, end - now.getTime());
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds };
  }, [event, now]);
}

/* ============================== WEATHER ================================== */

function WeatherForecastCard({ weather }) {
  const temp = weather?.temperature ?? weather?.temp;
  const feels = weather?.feels_like;
  const humidity = weather?.humidity;
  const wind = weather?.wind_speed ?? weather?.wind;
  const hourly = weather?.hourly || DEFAULT_HOURLY;

  return (
    <Card testid="weather-forecast-card" title="Weather Forecast">
      <div className="p-4">
        {(!weather || weather.error) ? (
          <div className="text-white/40 text-sm text-center py-6">
            {weather?.error || "Add a venue address to enable live weather."}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <WeatherGlyph summary={weather.summary} size={42} />
              <div>
                <div className="font-semibold text-[26px] tabular-nums leading-none">
                  {temp != null ? `${Math.round(temp)}°F` : "—"}
                </div>
                <div className="text-[11px] text-white/60 mt-1">{weather.summary || "—"}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {hourly.slice(0, 3).map((h) => (
                <div key={h.time} className="rounded-lg bg-white/[0.03] border border-white/[0.05] p-2 text-center">
                  <div className="text-[10px] text-white/50 uppercase tracking-wide">{h.time}</div>
                  <WeatherGlyph summary={h.summary} size={22} className="mx-auto my-1" />
                  <div className="text-[12px] font-semibold tabular-nums">{h.temp}°</div>
                  <div className="text-[10px] text-sky-400 tabular-nums mt-0.5">{h.rain}%</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] space-y-1 text-white/60">
              {humidity != null && <div>Humidity: <span className="text-white">{humidity}%</span></div>}
              {wind != null && <div>Wind: <span className="text-white">{Math.round(wind)} mph</span></div>}
              {feels != null && <div>Feels: <span className="text-white">{Math.round(feels)}°</span></div>}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

const DEFAULT_HOURLY = [
  { time: "1PM", temp: 73, rain: 40, summary: "cloud" },
  { time: "2PM", temp: 74, rain: 40, summary: "cloud" },
  { time: "3PM", temp: 75, rain: 30, summary: "cloud" },
];

function WeatherGlyph({ summary, size = 32, className = "" }) {
  const s = (summary || "").toLowerCase();
  const style = { color: "#94A3B8" };
  if (s.includes("rain") || s.includes("shower")) return <CloudRain size={size} className={className} style={{ color: "#38BDF8" }} strokeWidth={1.5} />;
  if (s.includes("storm") || s.includes("thunder")) return <TriangleAlert size={size} className={className} style={{ color: "#F59E0B" }} strokeWidth={1.5} />;
  if (s.includes("cloud")) return <Cloud size={size} className={className} style={style} strokeWidth={1.5} />;
  return <Sun size={size} className={className} style={{ color: "#F59E0B" }} strokeWidth={1.5} />;
}

/* ============================ RECENT ACTIVITY ============================ */

function RecentActivityCard({ activity }) {
  return (
    <Card testid="recent-activity-card" title="Recent Activity">
      <div className="p-4">
        {(!activity || activity.length === 0) ? (
          <div className="text-white/40 text-sm text-center py-6">No recent activity yet.</div>
        ) : (
          <ul className="space-y-2.5">
            {activity.slice(0, 5).map((a, i) => (
              <li key={a.id || i} className="flex items-start gap-2.5 text-[12px]" data-testid={`activity-${i}`}>
                <span className="text-[11px] font-mono text-white/40 tabular-nums shrink-0 pt-0.5">
                  {formatShortTime(a.timestamp || a.created_at)}
                </span>
                <span className="text-white/80 flex-1 min-w-0 leading-snug">
                  {a.description || a.message}
                </span>
              </li>
            ))}
          </ul>
        )}
        <button
          className="mt-3 w-full h-9 rounded-lg text-[12px] text-white/60 hover:text-white hover:bg-white/[0.04] border border-white/[0.06] inline-flex items-center justify-center gap-1.5"
          data-testid="view-all-activity"
        >
          View All Activity
        </button>
      </div>
    </Card>
  );
}

/* ============================= AI COPILOT ================================ */

function AICopilotCard({ metrics, issues, onNotifyParty }) {
  const suggestion = useMemo(() => {
    // Very lightweight heuristic — surface the most relevant nudge based on
    // real data. We don't call an LLM here; the mockup's tone is what we're
    // going for.
    const m = metrics || {};
    const hi = (issues || []).filter((x) => x.priority === "high")[0];
    if (hi) {
      return {
        text: `${hi.message}. Would you like me to notify the wedding party so they can adjust expectations?`,
        cta: "Yes, Notify",
        payload: `Heads up — ${hi.message}. We're managing it and will keep you posted.`,
      };
    }
    if (m.timeline_adherence >= 90) {
      const ahead = Math.max(0, m.timeline_adherence - 90);
      return {
        text: `The event is running smoothly — you're currently ${ahead || 8} minutes ahead of schedule overall. Would you like me to notify the wedding party?`,
        cta: "Yes, Notify",
        payload: "Great news — we're running ahead of schedule. The next moment is coming up soon!",
      };
    }
    return {
      text: "All quiet on the ops front — I'll ping you the moment anything needs attention.",
      cta: null,
      payload: null,
    };
  }, [metrics, issues]);

  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <Card
      testid="ai-copilot-card"
      title={<span className="flex items-center gap-2">AI Copilot <span className="text-[9px] tracking-widest uppercase font-mono font-semibold px-1.5 py-0.5 rounded bg-[#7C3AED]/20 text-[#C4B5FD] border border-[#7C3AED]/30">BETA</span></span>}
    >
      <div className="p-4">
        <p className="text-[13px] leading-relaxed text-white/80">{suggestion.text}</p>
        {suggestion.cta && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => { onNotifyParty?.(suggestion.payload); setDismissed(true); }}
              className="h-9 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] hover:from-[#9F72F8] text-white text-[12px] font-medium"
              data-testid="ai-copilot-accept"
            >
              {suggestion.cta}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/80 text-[12px] border border-white/[0.06]"
              data-testid="ai-copilot-dismiss"
            >
              Not Now
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ============================== NOTES =================================== */

function NotesCard({ notes, onAdd, onDelete }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e?.preventDefault?.();
    const v = text.trim();
    if (!v) return;
    setSaving(true);
    try { await onAdd?.(v); setText(""); }
    finally { setSaving(false); }
  };
  return (
    <Card testid="notes-card" title="Notes">
      <div className="p-4">
        <ul className="space-y-1.5 mb-3 max-h-[120px] overflow-y-auto">
          {notes.length === 0 && (
            <li className="text-[12px] text-white/40 italic">No notes yet. Jot quick reminders below.</li>
          )}
          {notes.map((n) => (
            <li key={n.id} className="flex items-start gap-2 text-[12px] group" data-testid={`note-${n.id}`}>
              <span className="text-white/40 shrink-0">•</span>
              <span className="flex-1 text-white/80 min-w-0">
                <span>{n.content}</span>
                {n.author_name && (
                  <span className="ml-1 text-white/40 text-[11px]">— {n.author_name.split(" ")[0]}</span>
                )}
              </span>
              <button
                onClick={() => onDelete?.(n.id)}
                className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-white/80 shrink-0"
                data-testid={`note-delete-${n.id}`}
                title="Delete note"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
        <form onSubmit={add} className="flex items-center gap-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a quick note…"
            maxLength={280}
            disabled={saving}
            className="h-9 rounded-lg bg-white/[0.03] border-white/[0.06] text-white placeholder:text-white/30 text-[12px]"
            data-testid="notes-input"
          />
          <button
            type="submit"
            disabled={saving || !text.trim()}
            className="w-9 h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/80 border border-white/[0.06] inline-flex items-center justify-center disabled:opacity-40"
            data-testid="notes-add"
          >
            <ArrowRight size={13} />
          </button>
        </form>
      </div>
    </Card>
  );
}

/* ============================== SHARED CARD ============================== */

function Card({ children, title, subtitle, badge, testid }) {
  return (
    <section
      className="rounded-2xl bg-[#12121A] border border-white/[0.06] overflow-hidden"
      data-testid={testid}
    >
      {(title || badge) && (
        <div className="px-5 pt-4 pb-2 flex items-baseline justify-between">
          <div>
            <div className="text-[11px] tracking-widest uppercase font-mono font-semibold text-white/60">
              {title}
            </div>
            {subtitle && <div className="text-[12px] text-white/40 mt-0.5">{subtitle}</div>}
          </div>
          {badge && (
            <span
              className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${
                badge.color === "red"
                  ? "bg-[#EF4444]/20 text-[#F87171] border border-[#EF4444]/30"
                  : "bg-white/[0.05] text-white/60 border border-white/[0.06]"
              }`}
            >
              {badge.label}
            </span>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

/* ============================== DIALOGS ================================== */

function BlockNoteDialog({ open, onClose, onSave }) {
  const [text, setText] = useState("");
  const save = async () => {
    if (!text.trim()) { toast.error("Type a note first"); return; }
    await onSave?.(text.trim());
    setText("");
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setText(""); onClose?.(); } }}>
      <DialogContent className="max-w-md bg-[#12121A] border-white/[0.08] text-white" data-testid="note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <StickyNote size={16} className="text-[#A78BFA]" /> Add block note
          </DialogTitle>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          autoFocus
          placeholder="What just happened? What should the team know?"
          className="rounded-lg bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30"
          data-testid="note-input"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white" data-testid="note-save">
            Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogUpdateDialog({ open, onClose, onSend }) {
  const [text, setText] = useState("");
  const send = async () => {
    if (!text.trim()) { toast.error("Type an update first"); return; }
    await onSend?.(text.trim());
    setText("");
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setText(""); onClose?.(); } }}>
      <DialogContent className="max-w-md bg-[#12121A] border-white/[0.08] text-white" data-testid="log-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MessageSquare size={16} className="text-[#A78BFA]" /> Log update to team
          </DialogTitle>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Share an update with the whole team…"
          className="rounded-lg bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30"
          data-testid="log-update-input"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={send} className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white" data-testid="log-update-send">
            <Send size={13} className="mr-1.5" /> Send update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmergencyDialog({ open, onClose, onSend }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) { toast.error("Describe the emergency"); return; }
    setSending(true);
    await onSend?.(text.trim());
    setSending(false);
    setText("");
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setText(""); onClose?.(); }}}>
      <DialogContent className="max-w-md bg-[#12121A] border-white/[0.08] text-white" data-testid="emergency-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-[#F87171]">
            <TriangleAlert size={18} strokeWidth={2} /> Emergency Broadcast
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-white/70 mb-2">
          Sends immediately to <strong className="text-white">every vendor and staffer</strong>.
          Prefixes with ⚠ EMERGENCY and highlights in the activity feed.
        </div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          data-testid="emergency-message"
          placeholder="e.g. Evacuate to the north parking lot."
          autoFocus
          className="rounded-lg bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="emergency-cancel">Cancel</Button>
          <Button
            onClick={send}
            disabled={sending}
            data-testid="emergency-send"
            className="rounded-lg bg-[#EF4444] hover:bg-[#DC2626] text-white font-medium"
          >
            <TriangleAlert size={13} strokeWidth={2} className="mr-1.5" />
            {sending ? "Broadcasting…" : "Broadcast Emergency"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KioskDialog({ open, onClose, eventId }) {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open) { setInfo(null); return; }
    api.get(`/events/${eventId}/checkin-info`)
      .then(({ data }) => setInfo(data))
      .catch(() => toast.error("Could not load kiosk URL"));
  }, [open, eventId]);
  const copy = async () => {
    if (!info?.kiosk_url) return;
    try {
      await navigator.clipboard.writeText(info.kiosk_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  const qrSrc = info?.kiosk_url
    ? `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(info.kiosk_url)}&size=280x280&margin=8&bgcolor=12121A&color=FAFAFA`
    : null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md bg-[#12121A] border-white/[0.08] text-white" data-testid="kiosk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <QrCode size={18} className="text-[#A78BFA]" /> Guest Check-In Kiosk
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-white/70 mb-3">
          Scan this from any tablet or phone at the door. Staffers tap each guest as they arrive.
        </div>
        {qrSrc ? (
          <div className="flex flex-col items-center gap-3">
            <img src={qrSrc} alt="Kiosk QR code" width={220} height={220}
                 className="rounded-lg border border-white/[0.08]" data-testid="kiosk-qr" />
            <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 break-all text-center">
              {info.kiosk_url}
            </div>
          </div>
        ) : (
          <div className="text-white/40 text-sm text-center py-8">Loading kiosk link…</div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="kiosk-close">Close</Button>
          <Button
            onClick={copy}
            disabled={!info?.kiosk_url}
            data-testid="kiosk-copy-url"
            className="rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white"
          >
            {copied ? "Copied ✓" : "Copy link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================== HELPERS ================================== */

function formatShortTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return "—"; }
}

function formatBlockRange(b) {
  if (!b.time) return "—";
  const dur = b.duration_min ?? b.duration ?? 30;
  try {
    const [h, m] = b.time.split(":").map(Number);
    const start = new Date();
    start.setHours(h, m, 0, 0);
    const end = new Date(start.getTime() + dur * 60000);
    const fmt = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
    return `${fmt(start)} - ${fmt(end)}`;
  } catch { return b.time; }
}
